import { useCallback } from "react";
import { type models } from "jiffy-api";
import { MadeAddToCartDocument } from "graphql-api/operations";
import type { AddMadeToCartInput, PlacementArtworkPosition } from "graphql-api/types";
import type { DesignLabFormType } from "@design-lab/shared/types";
import { useIsSample, useDesignLabConfig } from "@design-lab/shared/lib/config";
import {
  trackAddToCartError,
  buildAddToCartAnalyticsData,
  sendAddToCartAnalytics
} from "@design-lab/shared/lib/analytics";
import { useCurrentComposition } from "@design-lab/entities/composition/model/useCurrentComposition";
import { useCompositionPricing, useCompositionsList } from "@design-lab/entities/composition/model";
import { useMutation } from "@design-lab/shared/graphql";
import { PLACEMENTS } from "@design-lab/shared/constants";
import { useAtomValue } from "jotai";
import {
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pinningSessionsAtom,
  type DesignLabPlacementKey
} from "@design-lab/entities/pinning";

export function useAddToCartSubmit() {
  const [addToCart] = useMutation(MadeAddToCartDocument);
  const { currentComposition } = useCurrentComposition();
  const isSample = useIsSample();
  const config = useDesignLabConfig();
  const { getVariantPrice } = useCompositionPricing();
  const { products, getCompositionById } = useCompositionsList();
  const pinningSessions = useAtomValue(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);

  const executeAddToCart = useCallback(
    async (data: DesignLabFormType) => {
      const variants = data.compositions.addedCompositions.flatMap(composition =>
        Object.entries(composition.variants)
          .filter(([_, quantity]) => typeof quantity === "number" && quantity > 0)
          .map(([variantId, quantity]) => ({
            variantId,
            quantity: quantity as number
          }))
      );

      // Fill placements array
      const placementsToAddToCart = PLACEMENTS.filter(
        placement => placement === "back" || data.artwork.selectedFrontPlacement === placement
      );

      const placementsInput = placementsToAddToCart
        .map(placement => {
          const placementData = data.artwork[placement];

          if (!placementData?.artworkId || !placementData?.size) {
            return null;
          }

          const placementInfo = data.artwork.info[placement === "back" ? "back" : "front"];
          const position = placement === "frontLeftChest" ? "front_left_chest" : placement;

          // Get pinning session ID only if session is completed
          const placementKey = placement as DesignLabPlacementKey;
          const { session } = getPinningSessionForPlacement({
            placementKey,
            designProjectPlacementIds,
            pinningSessions
          });
          const pinningSessionId = session?.state === "completed" ? session.sessionId : undefined;

          return {
            position: position as PlacementArtworkPosition,
            width: placementData.size.width,
            height: placementData.size.height,
            dtfArtwork: {
              id: placementData.artworkId,
              enhancementTier: placementInfo.enhancementTier,
              removeBackground: placementInfo.removeBackground,
              editImageManual: placementInfo.editImageManual || undefined,
              enhancementRequestNote: placementInfo.enhancementRequestNote || undefined,
              jiffyDesignServices: placementInfo.jiffyDesignServices || undefined,
              ...(placementData.llmRedrawRequestId && {
                llmRedrawRequestId: placementData.llmRedrawRequestId.toString()
              }),
              ...(pinningSessionId && { pinningSessionId })
            }
          };
        })
        .filter((placement): placement is NonNullable<typeof placement> => placement !== null);

      const timestamp = Math.floor(Date.now() / 1000);
      const orderNumber = config.orderNumber || "";
      const eventId = `${orderNumber}:${timestamp}:design-lab`;

      const input: AddMadeToCartInput = {
        variants,
        placements: placementsInput,
        sample: isSample,
        eventId,
        designProjectUuid: data.artwork.designProjectUuid
      };

      // Check for zero dimensions in artworks and log to Honeybadger
      const placementsWithZeroDimensions = placementsInput.filter(
        placement => placement.width <= 0 || placement.height <= 0
      );

      if (placementsWithZeroDimensions.length > 0) {
        window.Honeybadger?.notify("Design Lab: Artwork with zero dimensions", {
          params: {
            input,
            placementsWithZeroDimensions,
            orderNumber,
            eventId
          }
        });
      }

      const result = await addToCart({
        variables: { input },
        errorPolicy: "none" // This will make GraphQL errors throw
      });

      if (!result.data?.addMadeToCart) {
        throw new Error(
          `Failed to add to cart: ${JSON.stringify(input)}; ${JSON.stringify(result)};`
        );
      }

      const response = result.data.addMadeToCart;

      // Update current URL with edit mode params before navigating to cart
      if (response.assemblyGroupId) {
        const compositionId = currentComposition?.id;
        const params = new URLSearchParams();

        params.set("edit", "true");
        params.set("asgid", response.assemblyGroupId.toString());
        params.set("compositionid", compositionId?.toString() || "");

        const newUrl = `/design-lab?${params.toString()}`;

        // Replace current history entry with edit mode URL
        window.history.replaceState(
          {
            assemblyGroupId: response.assemblyGroupId,
            isEditMode: true,
            compositionId: compositionId || ""
          },
          "",
          newUrl
        );
      }

      // Build analytics data
      const analyticsData = buildAddToCartAnalyticsData({
        compositions: data.compositions.addedCompositions,
        products,
        getVariantPrice,
        getCompositionById,
        config,
        eventId
      });

      // Send analytics
      sendAddToCartAnalytics(analyticsData, isSample);

      return {
        assemblyGroupId: response.assemblyGroupId,
        lineItems: response.lineItems.reduce(
          (acc: Record<string, number>, item: any) => {
            acc[item.lineItemId.toString()] = item.quantity;
            return acc;
          },
          {} as Record<string, number>
        ),
        blanks: null
      } as models.cart.madeAdd.Response;
    },
    [
      config,
      isSample,
      addToCart,
      products,
      getVariantPrice,
      getCompositionById,
      designProjectPlacementIds,
      pinningSessions,
      currentComposition?.id
    ]
  );

  const handleAddToCart = useCallback(
    async (data: DesignLabFormType) => {
      try {
        const response = await executeAddToCart(data);
        return response;
      } catch (error) {
        trackAddToCartError(data, String(error), error);
        throw error;
      }
    },
    [executeAddToCart]
  );

  return {
    placements: PLACEMENTS,
    handleAddToCart
  };
}
